'use client';

import { useRouter } from '@bprogress/next';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';

import { getCart } from '@/api/backend/service/cart';
import { getProfile } from '@/api/backend/service/profile';
import { getWishlist } from '@/api/backend/service/wishlist';
import { apiGtm } from '@/api/gtm';
import { storeCart } from '@/store/cart';
import { useUserStore } from '@/store/user';
import { storeWishlist } from '@/store/wishlist';
import { storeUiModal } from '@/store-ui/modal';
import { UiKitDrawer } from '@/ui-kit/drawer';
import { clearProductSizes } from '@/util/product-size-storage';

import { AuthCodeForm, LoginForm, RegistrationForm } from './components';
import { styles } from './FeatureAuthModal.styles';

export function FeatureAuthModal() {
  const t = useTranslations();
  const router = useRouter();
  const slots = styles();

  const modal = useStore(storeUiModal, (state) => state.modals.authLogin);
  const close = useStore(storeUiModal, (state) => state.close);

  const [step, setStep] = useState<'login' | 'code' | 'registration'>('login');
  const [phone, setPhone] = useState('');
  const [authSessionId, setAuthSessionId] = useState('');
  const [prevIsOpen, setPrevIsOpen] = useState(false);
  const isAuthCompletedRef = useRef(false);

  const isOpen = Boolean(modal?.isOpen);
  const placement = modal?.data?.placement ?? 'header';
  const authContext = modal?.data?.authContext ?? 'account';

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    setAuthSessionId(isOpen ? crypto.randomUUID() : '');
  }

  const setIsAuthorized = useUserStore((state) => state.setIsAuthorized);
  const setIsEmployee = useUserStore((state) => state.setIsEmployee);

  useEffect(() => {
    if (!authSessionId) return;

    isAuthCompletedRef.current = false;

    apiGtm.authOpen({
      event_label: 'popup',
      auth_context: authContext
    });
  }, [authSessionId, authContext]);

  if (!modal?.isOpen) return null;

  const redirectUrl =
    modal.data.redirectUrl?.startsWith('/') && !modal.data.redirectUrl.startsWith('//')
      ? modal.data.redirectUrl
      : undefined;

  const syncAuthorizedStores = async () => {
    const profileResponse = await getProfile();

    if (profileResponse.isError || !profileResponse.data) {
      return false;
    }

    const [cartResponse, wishlistResponse] = await Promise.all([
      getCart(),
      getWishlist()
    ]);

    if (
      cartResponse.isError ||
      !cartResponse.data ||
      wishlistResponse.isError ||
      !wishlistResponse.data
    ) {
      return false;
    }

    setIsAuthorized(true);
    clearProductSizes();
    setIsEmployee(profileResponse.data.isEmployee);
    storeCart.getState().setCart(cartResponse.data);
    storeWishlist.getState().setTotalItems(wishlistResponse.data.total);

    return true;
  };

  const handleLoginSuccess = (phoneNumber: string) => {
    apiGtm.authStart({
      auth_session_id: authSessionId,
      auth_method: 'flashing_call',
      auth_context: authContext
    });
    apiGtm.authCodeSent({
      auth_session_id: authSessionId,
      auth_method: 'flashing_call',
      provider: 'flashing_call'
    });

    setPhone(phoneNumber);
    setStep('code');
  };

  const handleBackToLogin = () => {
    setStep('login');
  };

  const handleClose = () => {
    if (!isAuthCompletedRef.current && authSessionId) {
      apiGtm.authCancel({
        auth_session_id: authSessionId,
        auth_context: authContext
      });
    }

    close('authLogin');
    setStep('login');
    setPhone('');
  };

  const completeAuth = () => {
    isAuthCompletedRef.current = true;
    if (redirectUrl || modal.data.needRedirect) {
      router.push(redirectUrl ?? '/account');
    } else {
      router.refresh();
    }
    handleClose();
  };

  const handleCodeSuccess = async (needsRegistration: boolean) => {
    if (needsRegistration) {
      isAuthCompletedRef.current = true;
      setIsAuthorized(true);
      clearProductSizes();
      setStep('registration');
      return true;
    }

    const isSynced = await syncAuthorizedStores();
    if (!isSynced) return false;

    completeAuth();

    return true;
  };

  const handleRegistrationSuccess = async () => {
    const isSynced = await syncAuthorizedStores();
    if (!isSynced) return false;

    completeAuth();

    return true;
  };

  return (
    <UiKitDrawer
      open
      contentClassName={slots.content({ step })}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      title={t('login.title')}
      direction="right"
      withoutMobile
      isUseHack
      allowExternalFocus
      autoFocus={false}
      handleOnly
    >
      {step === 'login' && (
        <LoginForm
          phone={phone}
          placement={placement}
          redirectUrl={redirectUrl}
          onSuccess={handleLoginSuccess}
        />
      )}
      {step === 'code' && (
        <AuthCodeForm
          phone={phone}
          placement={placement}
          redirectUrl={redirectUrl}
          authSessionId={authSessionId}
          authContext={authContext}
          onSuccess={handleCodeSuccess}
          onBack={handleBackToLogin}
        />
      )}
      {step === 'registration' && (
        <RegistrationForm
          onSuccess={handleRegistrationSuccess}
          onClose={handleClose}
        />
      )}
    </UiKitDrawer>
  );
}
